/**
 * Auth Store (Application)
 *
 * Global authentication state management using Zustand.
 * Handles user session, auth status, and auth-related UI state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { User } from '@/modules/user/domain/types';

import { AuthStatus } from '../../domain/types';
import { tokenStorage } from '../../infrastructure/services';

// ============================================================================
// Store Types
// ============================================================================

interface AuthState {
  status: AuthStatus;
  user: User | null;
  error: string | null;
  isInitialized: boolean;
}

interface AuthActions {
  /**
   * Set authenticated user
   */
  setUser: (user: User) => void;

  /**
   * Set auth status
   */
  setStatus: (status: AuthStatus) => void;

  /**
   * Set auth error
   */
  setError: (error: string | null) => void;

  /**
   * Mark auth as initialized (checked)
   */
  setInitialized: () => void;

  /**
   * Set user as authenticated with user data
   */
  setAuthenticated: (user: User) => void;

  /**
   * Update authenticated user's status
   */
  updateUserStatus: (status: string) => void;

  /**
   * Clear all auth state
   */
  clearAuth: () => void;

  /**
   * Reset error state
   */
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

// ============================================================================
// Initial State
// ============================================================================

const getInitialState = (): AuthState => {
  // Check if we have a stored token to determine initial status
  const hasToken = !!tokenStorage.getAccessToken();
  const cachedUser = tokenStorage.getUserData<User>();

  return {
    status: hasToken ? AuthStatus.Authenticated : AuthStatus.Unauthenticated,
    user: cachedUser,
    error: null,
    isInitialized: false,
  };
};

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthStore>()(
  devtools(
    immer((set) => ({
      ...getInitialState(),

      setUser: (user) =>
        set((state) => {
          state.user = user;
          state.status = AuthStatus.Authenticated;
          state.error = null;
        }),

      setStatus: (status) =>
        set((state) => {
          state.status = status;
        }),

      setError: (error) =>
        set((state) => {
          state.error = error;
          state.status = AuthStatus.Error;
        }),

      setInitialized: () =>
        set((state) => {
          state.isInitialized = true;
        }),

      setAuthenticated: (user) =>
        set((state) => {
          state.user = user;
          state.status = AuthStatus.Authenticated;
          state.error = null;
          state.isInitialized = true;
        }),

      updateUserStatus: (status) =>
        set((state) => {
          if (state.user) {
            state.user.status = status;
          }
        }),

      clearAuth: () =>
        set((state) => {
          state.user = null;
          state.status = AuthStatus.Unauthenticated;
          state.error = null;
        }),

      clearError: () =>
        set((state) => {
          state.error = null;
          if (state.status === AuthStatus.Error) {
            state.status = AuthStatus.Idle;
          }
        }),
    })),
    { name: 'auth-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * Check if user is authenticated
 */
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.status === AuthStatus.Authenticated);

/**
 * Get auth status
 */
export const useAuthStatus = () =>
  useAuthStore((state) => ({
    status: state.status,
    isLoading: state.status === AuthStatus.Loading,
    isAuthenticated: state.status === AuthStatus.Authenticated,
    isError: state.status === AuthStatus.Error,
    error: state.error,
  }));

/**
 * Check if auth has been initialized
 */
export const useIsAuthInitialized = () =>
  useAuthStore((state) => state.isInitialized);

/**
 * Get current user's extension type (e.g. web_rtc)
 */
export const useExtensionType = () =>
  useAuthStore((state) => state.user?.extensionType);
